'use client';

import { useEffect, useRef } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import BestsellerIcon from './BestsellerIcon';
import { BESTSELLER_RANKING_TAB_CLASS_NAME } from './bestsellerStyles';
import styles from './RankingTabs.module.css';
import type { BestsellerRankingTab } from './types';

interface RankingTabsProps {
  tabs: BestsellerRankingTab[];
  activeTabId: string;
  onChange: (tabId: string) => void;
}

function RankingTabButton({
  tab,
  active,
  onClick,
}: {
  tab: BestsellerRankingTab;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={BESTSELLER_RANKING_TAB_CLASS_NAME}
      aria-current={active ? 'true' : undefined}
      onClick={onClick}
    >
      {tab.icon ? (
        <BestsellerIcon src={tab.icon} className={`h-[24px] w-[24px] ${styles.tabIcon}`} />
      ) : null}
      <span className="truncate">{tab.label}</span>
      <span
        aria-hidden
        data-ranking-tab-indicator={active ? 'active' : 'inactive'}
        className={`pointer-events-none absolute bottom-0 left-0 z-[1] w-full ${
          active
            ? 'h-[4px] bg-[#463ae8] md:h-[2px] xl:h-[3px] 2xl:h-[4px]'
            : 'h-[2px] bg-[#d1d9e0] md:h-[0.8px]'
        }`}
      />
    </button>
  );
}

export default function RankingTabs({ tabs, activeTabId, onChange }: RankingTabsProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const activeIndex = Math.max(
    tabs.findIndex((tab) => tab.id === activeTabId),
    0,
  );
  const hasScrollableTabs = tabs.length === 3;
  const mobileSlideClassName =
    tabs.length === 1 ? '!w-full' : tabs.length === 2 ? '!w-1/2' : '!w-[160px]';

  useEffect(() => {
    if (!hasScrollableTabs) return;
    swiperRef.current?.slideTo(activeIndex);
  }, [activeIndex, hasScrollableTabs]);

  const handleChange = (tabId: string, index: number) => {
    onChange(tabId);
    if (hasScrollableTabs) swiperRef.current?.slideTo(index);
  };

  return (
    <nav className="h-[60px] overflow-hidden bg-[#f7f7f7] md:h-[56px] xl:h-[70px] 2xl:h-[104px]">
      <div className="h-full md:hidden">
        <Swiper
          className="h-full"
          slidesPerView="auto"
          slidesPerGroup={1}
          allowTouchMove={hasScrollableTabs}
          speed={300}
          initialSlide={activeIndex}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
        >
          {tabs.map((tab, index) => (
            <SwiperSlide key={tab.id} className={mobileSlideClassName}>
              <RankingTabButton
                tab={tab}
                active={tab.id === activeTabId}
                onClick={() => handleChange(tab.id, index)}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <div
        className="hidden h-full md:grid"
        style={{ gridTemplateColumns: `repeat(${Math.max(tabs.length, 1)}, minmax(0, 1fr))` }}
      >
        {tabs.map((tab) => (
          <RankingTabButton
            key={tab.id}
            tab={tab}
            active={tab.id === activeTabId}
            onClick={() => onChange(tab.id)}
          />
        ))}
      </div>
    </nav>
  );
}
